import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { listPublicSpanishBlogPosts } from "@/lib/blog.functions";
import { buildHead } from "@/lib/seo";
import { setEdgeHtmlCache } from "@/lib/cache-headers";

export const Route = createFileRoute("/es/blog")({
  loader: async ({ location }) => {
    if (location.pathname.replace(/\/$/, "") !== "/es/blog") return { posts: [], isIndex: false };
    await setEdgeHtmlCache();
    const data = await listPublicSpanishBlogPosts();
    return { ...data, isIndex: true };
  },
  head: () => {
    const head = buildHead({
    locale: "es", title: "Blog de ingeniería Shopify · Shugert",
    description: "Notas técnicas sobre Shopify, migraciones, integraciones, rendimiento y SEO para equipos de comercio electrónico.",
    path: "/es/blog", altPath: "/es/blog",
    });
    return { ...head, links: head.links.filter((link) => link.rel !== "canonical") };
  },
  component: SpanishBlog,
});

// Covers live at /assets/blog/es/<slug>.jpg with -800 and .webp siblings.
// Cards are at most ~380px wide, so the 800w files cover 2x screens.
function CardCover({ src, alt, eager }: { src: string; alt: string; eager: boolean }) {
  const m = src.match(/^\/assets\/blog\/es\/([a-z0-9-]+)\.jpg$/);
  const loading = eager ? "eager" : "lazy";
  const sizes = "(min-width: 1024px) 380px, (min-width: 768px) 50vw, 100vw";
  if (!m) return <img src={src} alt={alt} width={1600} height={900} loading={loading} decoding="async" className="aspect-video h-auto w-full object-cover" />;
  const base = `/assets/blog/es/${m[1]}`;
  return (
    <picture>
      <source type="image/webp" srcSet={`${base}-800.webp 800w, ${base}.webp 1600w`} sizes={sizes} />
      <img src={`${base}-800.jpg`} srcSet={`${base}-800.jpg 800w, ${base}.jpg 1600w`} sizes={sizes} alt={alt} width={1600} height={900} loading={loading} fetchPriority={eager ? "high" : undefined} decoding="async" className="aspect-video h-auto w-full border-b border-border object-cover" />
    </picture>
  );
}

function SpanishBlog() {
  const { pathname } = useLocation();
  const { posts } = Route.useLoaderData();
  if (pathname.replace(/\/$/, "") !== "/es/blog") return <Outlet />;
  return <SiteShell><link rel="canonical" href="https://shugert.com.mx/es/blog" /><section className="mx-auto max-w-6xl px-6 py-16">
    <p className="text-sm uppercase tracking-widest text-muted-foreground">Shugert · México</p>
    <h1 className="mt-4 font-display text-4xl font-semibold sm:text-5xl">Notas técnicas de Shopify</h1>
    <p className="mt-5 max-w-2xl text-lg text-muted-foreground">Ingeniería, integraciones, migraciones, rendimiento y SEO para equipos de comercio electrónico.</p>
    <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((p: any, i: number) => <article key={p.slug_es} className="overflow-hidden rounded-3xl border border-border">
        {p.featured_image_es && <a href={`/es/blog/${p.slug_es}`} tabIndex={-1} aria-hidden="true" className="block">
          <CardCover src={p.featured_image_es} alt={p.featured_image_alt_es || p.title_es} eager={i === 0} />
        </a>}
        <div className="p-6">
        <time dateTime={p.published_at} className="text-sm text-muted-foreground">{new Intl.DateTimeFormat("es-MX", { day:"numeric", month:"long", year:"numeric", timeZone:"America/Mexico_City" }).format(new Date(p.published_at))}</time>
        <h2 className="mt-3 text-xl font-semibold"><a href={`/es/blog/${p.slug_es}`} className="hover:underline">{p.title_es}</a></h2>
        {p.excerpt_es && <p className="mt-3 text-muted-foreground">{p.excerpt_es}</p>}
        </div>
      </article>)}
    </div>
    {posts.length === 0 && <p className="mt-10">Próximamente publicaremos nuevas notas técnicas.</p>}
  </section></SiteShell>;
}
